import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  increment,
  getDocs,
  query,
  where,
  collection,
  documentId,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../config/firebase";

/**
 * Serviço para gerenciar interações com devocionais
 * - Visualizações (sem repetir do mesmo usuário)
 * - Curtidas (permitir curtir/descurtir)
 */

class DevocionalInteracaoService {
  /**
   * Registrar visualização do devocional
   * @param {string} devocionalId - ID do devocional (data no formato YYYY-MM-DD)
   * @param {string} userId - ID do usuário
   * @returns {Promise<boolean>} - true se registrou nova visualização, false se já tinha visto
   */
  async registrarVisualizacao(devocionalId, userId) {
    try {
      if (!userId) {
        console.warn("[DevocionalInteracao] userId não fornecido");
        return false;
      }

      const interacaoRef = doc(db, "devocionais_interacoes", devocionalId);
      const interacaoSnap = await getDoc(interacaoRef);

      if (interacaoSnap.exists()) {
        const dados = interacaoSnap.data();

        // Verificar se usuário já visualizou
        if (dados.visualizacoes?.includes(userId)) {
          return false; // Já tinha visto, não registra novamente
        }

        // Adicionar usuário à lista de visualizações
        await updateDoc(interacaoRef, {
          visualizacoes: arrayUnion(userId),
          totalVisualizacoes: increment(1),
          ultimaAtualizacao: new Date().toISOString(),
        });

        return true; // Nova visualização registrada
      } else {
        // Criar documento de interação pela primeira vez
        await setDoc(interacaoRef, {
          devocionalId,
          visualizacoes: [userId],
          totalVisualizacoes: 1,
          curtidas: [],
          totalCurtidas: 0,
          criadoEm: new Date().toISOString(),
          ultimaAtualizacao: new Date().toISOString(),
        });

        return true; // Nova visualização registrada
      }
    } catch (error) {
      console.error(
        "[DevocionalInteracao] Erro ao registrar visualização:",
        error
      );
      throw error;
    }
  }

  /**
   * Curtir devocional
   * @param {string} devocionalId - ID do devocional
   * @param {string} userId - ID do usuário
   * @returns {Promise<boolean>} - true se curtiu, false se descurtiu
   */
  async curtirDevocional(devocionalId, userId) {
    try {
      if (!userId) {
        throw new Error("userId não fornecido");
      }

      const interacaoRef = doc(db, "devocionais_interacoes", devocionalId);
      const interacaoSnap = await getDoc(interacaoRef);

      if (interacaoSnap.exists()) {
        const dados = interacaoSnap.data();

        // Verificar se usuário já curtiu
        if (dados.curtidas?.includes(userId)) {
          // Descurtir - remover usuário da lista
          await updateDoc(interacaoRef, {
            curtidas: arrayRemove(userId),
            totalCurtidas: increment(-1),
            ultimaAtualizacao: new Date().toISOString(),
          });

          return false; // Descurtiu
        } else {
          // Curtir - adicionar usuário à lista
          await updateDoc(interacaoRef, {
            curtidas: arrayUnion(userId),
            totalCurtidas: increment(1),
            ultimaAtualizacao: new Date().toISOString(),
          });

          return true; // Curtiu
        }
      } else {
        // Criar documento de interação com primeira curtida
        await setDoc(interacaoRef, {
          devocionalId,
          visualizacoes: [],
          totalVisualizacoes: 0,
          curtidas: [userId],
          totalCurtidas: 1,
          criadoEm: new Date().toISOString(),
          ultimaAtualizacao: new Date().toISOString(),
        });

        return true; // Curtiu
      }
    } catch (error) {
      console.error("[DevocionalInteracao] Erro ao curtir devocional:", error);
      throw error;
    }
  }

  /**
   * Verificar se usuário já curtiu o devocional
   * @param {string} devocionalId - ID do devocional
   * @param {string} userId - ID do usuário
   * @returns {Promise<boolean>} - true se já curtiu
   */
  async verificarCurtida(devocionalId, userId) {
    try {
      if (!userId) {
        return false;
      }

      const interacaoRef = doc(db, "devocionais_interacoes", devocionalId);
      const interacaoSnap = await getDoc(interacaoRef);

      if (interacaoSnap.exists()) {
        const dados = interacaoSnap.data();
        return dados.curtidas?.includes(userId) || false;
      }

      return false;
    } catch (error) {
      console.error("[DevocionalInteracao] Erro ao verificar curtida:", error);
      return false;
    }
  }

  /**
   * Obter estatísticas do devocional
   * @param {string} devocionalId - ID do devocional
   * @returns {Promise<Object>} - Objeto com totalVisualizacoes e totalCurtidas
   */
  async obterEstatisticas(devocionalId) {
    try {
      const interacaoRef = doc(db, "devocionais_interacoes", devocionalId);
      const interacaoSnap = await getDoc(interacaoRef);

      if (interacaoSnap.exists()) {
        const dados = interacaoSnap.data();
        return {
          totalVisualizacoes: dados.totalVisualizacoes || 0,
          totalCurtidas: dados.totalCurtidas || 0,
          visualizacoes: dados.visualizacoes || [],
          curtidas: dados.curtidas || [],
        };
      }

      return {
        totalVisualizacoes: 0,
        totalCurtidas: 0,
        visualizacoes: [],
        curtidas: [],
      };
    } catch (error) {
      console.error("[DevocionalInteracao] Erro ao obter estatísticas:", error);
      return {
        totalVisualizacoes: 0,
        totalCurtidas: 0,
        visualizacoes: [],
        curtidas: [],
      };
    }
  }

  /**
   * Obter todas as interações do usuário com o devocional
   * @param {string} devocionalId - ID do devocional
   * @param {string} userId - ID do usuário
   * @returns {Promise<Object>} - Objeto com jaCurtiu e jaVisualizou
   */
  async obterInteracoesUsuario(devocionalId, userId) {
    try {
      if (!userId) {
        return {
          jaCurtiu: false,
          jaVisualizou: false,
        };
      }

      const interacaoRef = doc(db, "devocionais_interacoes", devocionalId);
      const interacaoSnap = await getDoc(interacaoRef);

      if (interacaoSnap.exists()) {
        const dados = interacaoSnap.data();
        return {
          jaCurtiu: dados.curtidas?.includes(userId) || false,
          jaVisualizou: dados.visualizacoes?.includes(userId) || false,
        };
      }

      return {
        jaCurtiu: false,
        jaVisualizou: false,
      };
    } catch (error) {
      console.error(
        "[DevocionalInteracao] Erro ao obter interações do usuário:",
        error
      );
      return {
        jaCurtiu: false,
        jaVisualizou: false,
      };
    }
  }
  /**
   * Escutar interações em tempo real
   * @param {string} devocionalId - ID do devocional
   * @param {function} callback - Chamado com { totalVisualizacoes, totalCurtidas, visualizacoes, curtidas }
   * @returns {function} unsubscribe - Chamar para parar de escutar
   */
  escutarInteracoes(devocionalId, callback) {
    const interacaoRef = doc(db, "devocionais_interacoes", devocionalId);
    return onSnapshot(
      interacaoRef,
      (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          callback({
            totalVisualizacoes: d.totalVisualizacoes || 0,
            totalCurtidas: d.totalCurtidas || 0,
            visualizacoes: d.visualizacoes || [],
            curtidas: d.curtidas || [],
          });
        } else {
          callback({ totalVisualizacoes: 0, totalCurtidas: 0, visualizacoes: [], curtidas: [] });
        }
      },
      (error) => console.error("[DevocionalInteracao] Listener error:", error)
    );
  }

  /**
   * Buscar nomes dos usuários a partir de uma lista de IDs
   * @param {string[]} userIds - Lista de IDs de usuários
   * @returns {Promise<Array<{uid: string, nome: string}>>}
   */
  async buscarNomesUsuarios(userIds) {
    try {
      if (!userIds || userIds.length === 0) return [];

      const usersRef = collection(db, "users");
      const nomes = [];

      // Firestore 'in' query aceita no máximo 30 itens por vez
      const chunks = [];
      for (let i = 0; i < userIds.length; i += 30) {
        chunks.push(userIds.slice(i, i + 30));
      }

      for (const chunk of chunks) {
        const q = query(usersRef, where(documentId(), "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          nomes.push({
            uid: docSnap.id,
            nome: data.displayName || data.nome || data.email || "Usuário",
          });
        });
      }

      return nomes;
    } catch (error) {
      console.error("[DevocionalInteracao] Erro ao buscar nomes:", error);
      return [];
    }
  }
}

export default new DevocionalInteracaoService();
